/**
 * Stats pattern. See `patterns/stats.php`.
 *
 * Counts up the bold figure in each stat heading when it comes into view.
 * The count starts at zero and stops at the number in the markup.
 * The text around the number (for example "+") does not change.
 */

const DURATION = 2000;

/**
 * Makes the animation slow down near the end.
 * @param {number} progress A value from 0 to 1.
 * @returns {number}
 */
const easeOutCubic = (progress) => 1 - Math.pow(1 - progress, 3);

/**
 * Prepares one figure for the count.
 * Returns nothing when the figure has no number.
 * @param {Element} figure
 * @returns {{start: Function, finish: Function}|undefined}
 */
const setUp = (figure) => {
  const text = figure.textContent;
  const match = text.match(/\d[\d,]*(?:\.\d+)?/);
  if (!match) return;

  const [ number ] = match;
  const before = text.slice(0, match.index);
  const after = text.slice(match.index + number.length);
  const target = parseFloat(number.replace(/,/g, ""));
  const decimals = (number.split(".")[1] || "").length;

  // Keep the format of the markup. A year such as "1985" has no comma.
  const format = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: number.includes(","),
  });

  // The visible text changes during the count.
  // Until the count stops, the heading label gives the final text to assistive technology.
  // Do not replace a label that an author set.
  const heading = figure.closest("h2, h3, h4, h5, h6");
  const labeled = !heading.hasAttribute("aria-label");
  if (labeled) {
    heading.setAttribute("aria-label", heading.textContent.replace(/\s+/g, " ").trim());
  }

  let frame;

  const render = (value) => {
    figure.textContent = before + format.format(value) + after;
  };

  // Shows the final number at once and stops the count.
  const finish = () => {
    cancelAnimationFrame(frame);
    render(target);
    if (labeled) {
      heading.removeAttribute("aria-label");
    }
  };

  const start = () => {
    let startTime;

    const step = (time) => {
      startTime ??= time;
      const progress = Math.min((time - startTime) / DURATION, 1);

      if (progress < 1) {
        render(target * easeOutCubic(progress));
        frame = requestAnimationFrame(step);
      } else {
        finish();
      }
    };

    frame = requestAnimationFrame(step);
  };

  render(0);

  return { start, finish };
};

const figures = $$(".stats-item > :is(h2, h3, h4, h5, h6) strong");
const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;

// Show the final numbers when the user prefers reduced motion,
// or when the browser cannot tell that a figure is in view.
if (figures.length && !reduceMotion && "IntersectionObserver" in window) {
  const counts = new Map();

  // The fixed site header covers the top of the viewport.
  // The scroll padding on the root has the same height as the header.
  // Start a count only when the figure is below the header.
  const scrollPaddingTop = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      observer.unobserve(entry.target);
      counts.get(entry.target).start();
    }
  }, {
    rootMargin: `-${scrollPaddingTop}px 0px 0px 0px`,
    // Some browsers do not report a ratio of exactly 1 because of subpixel sizes.
    // Half of the figure in view is sufficient.
    threshold: 0.5,
  });

  for (const figure of figures) {
    const count = setUp(figure);
    if (!count) continue;

    counts.set(figure, count);
    observer.observe(figure);
  }

  // A printed page must show the final numbers, also for figures that are not yet in view.
  addEventListener("beforeprint", () => {
    observer.disconnect();
    for (const count of counts.values()) {
      count.finish();
    }
  });
}
